import React, { useState } from 'react';
import StandardDropdown from './StandardDropdown.jsx';

const STATUS_OPTIONS = [
  { value: '', label: 'Status: All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'stale', label: 'Stale' },
];

const COMPLIANCE_TYPE_OPTIONS = [
  { value: '', label: 'Compliance Type: All' },
  { value: 'building_code', label: 'Building Code' },
  { value: 'client_requirements', label: 'Client Requirements' },
  // { value: 'zoning', label: 'Zoning' }, // initial release — not offered
  { value: 'standards', label: 'Standards' },
];

/* Initial release: scans use compliance_check only — path type filter hidden for now.
const PATH_TYPE_OPTIONS = [
  { value: '', label: 'Path Type: All' },
  { value: 'conflict', label: 'Conflict' },
  { value: 'gap', label: 'Gap' },
  { value: 'compliance_check', label: 'Compliance Check' },
];
*/

const addIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const exportIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 16V4m0 12l-4-4m4 4l4-4M4 18v2a2 2 0 002 2h12a2 2 0 002-2v-2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function ComplianceFilterBar({
  filterStatus,
  onFilterStatusChange,
  filterComplianceType,
  onFilterComplianceTypeChange,
  filterPathType: _filterPathType,
  onFilterPathTypeChange: _onFilterPathTypeChange,
  filterAssigned,
  onFilterAssignedChange,
  projectMembers = [],
  query,
  onQueryChange,
  savedViews = [],
  onSaveView,
  onLoadView,
  onDeleteView,
  onExport,
  onOpenScanFilter,
  exportLabel = 'Export',
  saveViewLabel = 'Save view',
}) {
  const [viewName, setViewName] = useState('');

  const assigneeOptions = [
    { value: '', label: 'Assigned To: All' },
    ...(projectMembers || []).map((m) => ({ value: m.userId, label: m.name })),
    { value: 'unassigned', label: 'Unassigned' },
  ];

  const savedViewOptions =
    savedViews.length > 0
      ? [
          { value: '', label: 'Saved Views' },
          ...savedViews.map((v) => ({ value: v.id, label: v.name })),
        ]
      : [{ value: '', label: 'Saved Views', disabled: true }];

  const handleSave = () => {
    const name = viewName.trim() || `View ${savedViews.length + 1}`;
    onSaveView && onSaveView(name);
    setViewName('');
  };

  return (
    <div className="filter-bar">
      <span className="filter-label">Filter by</span>

      <StandardDropdown
        className="compliance-filter-select"
        variant="filter"
        value={filterStatus || ''}
        options={STATUS_OPTIONS}
        onChange={onFilterStatusChange}
        placeholder="Status: All"
      />

      <StandardDropdown
        className="compliance-filter-select"
        variant="filter"
        value={filterComplianceType || ''}
        options={COMPLIANCE_TYPE_OPTIONS}
        onChange={onFilterComplianceTypeChange}
        placeholder="Compliance Type: All"
      />

      {/* Path type filter — hidden while all scans use compliance_check
      <StandardDropdown
        className="compliance-filter-select"
        variant="filter"
        value={filterPathType || ''}
        options={PATH_TYPE_OPTIONS}
        onChange={onFilterPathTypeChange}
        placeholder="Path Type: All"
      />
      */}

      <StandardDropdown
        className="compliance-filter-select"
        variant="filter"
        value={filterAssigned || ''}
        options={assigneeOptions}
        onChange={onFilterAssignedChange}
        placeholder="Assigned To: All"
      />

      <button
        type="button"
        className="btn-primary btn-primary--small"
        onClick={onOpenScanFilter}
      >
        Filter by scan
      </button>
      <div className="filter-divider" />

      <StandardDropdown
        className="compliance-filter-select"
        variant="filter"
        value=""
        options={savedViewOptions}
        onChange={(id) => {
          if (id) onLoadView && onLoadView(id);
        }}
        placeholder="Saved Views"
      />

      {savedViews.length > 0 && onDeleteView && (
        <StandardDropdown
          className="compliance-filter-select"
          variant="filter"
          value=""
          options={[
            { value: '', label: 'Delete view…' },
            ...savedViews.map((v) => ({ value: `del:${v.id}`, label: `Delete: ${v.name}` })),
          ]}
          onChange={(id) => {
            if (id && String(id).startsWith('del:')) {
              const viewId = String(id).slice(4);
              const view = savedViews.find((v) => v.id === viewId);
              if (window.confirm(`Delete saved view "${view?.name || 'this view'}"? This cannot be undone.`)) {
                onDeleteView(viewId);
              }
            }
          }}
          placeholder="Delete view…"
        />
      )}

      <input
        type="text"
        className="save-view-name-input"
        placeholder="View name…"
        value={viewName}
        onChange={(e) => setViewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
          }
        }}
      />

      <button type="button" className="btn-primary btn-primary--small" onClick={handleSave}>
        {addIcon}
        {saveViewLabel}
      </button>

      <button type="button" className="btn-primary btn-primary--small" onClick={onExport}>
        {exportIcon}
        {exportLabel}
      </button>

      <div className="search-wrapper">
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
          <circle cx="5.5" cy="5.5" r="4" stroke="#999" strokeWidth="1.5" />
          <path d="M9 9l2.5 2.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          className="search-input"
          placeholder="Search issues…"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>
    </div>
  );
}
